import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ITEM_PRIORITIES, ITEM_PRIORITY_LABEL, ITEM_PRIORITY_STEP, formatPrice, type ItemDto } from '@wishare/shared';
import { faviconFor, hostOf, pluralize } from '../lib/format.js';
import { firstGrapheme, hueFromTitle } from '../lib/cover.js';
import { isFullyTaken } from '../lib/item-state.js';
import { Avatar, Badge, Button } from './ui.js';
import { IconDown, IconEdit, IconGiftOutline, IconTrash, IconUp } from './icons.js';

/**
 * Карточка желания.
 *
 * Одна и та же карточка выглядит по-разному для владельца и для дарителя —
 * и это принципиально. Владелец видит своё желание и кнопки правки. Даритель
 * видит то же самое плюс состояние брони.
 *
 * Компонент не решает, показывать ли брони: он просто рисует то, что пришло с
 * сервера. Если поле reservation пустое — блока броней нет вовсе. Решение
 * принимается один раз, на сервере, в domain/visibility.ts.
 */

interface ItemCardProps {
  item: ItemDto;
  /** Владелец списка: показываем правку вместо брони. */
  isOwner: boolean;
  canReserve: boolean;
  onEdit?: (item: ItemDto) => void;
  onDelete?: (item: ItemDto) => void;
  onReserve?: (item: ItemDto) => void;
  onCancelReservation?: (item: ItemDto) => void;
  busy?: boolean;
  /**
   * Перестановка желания в списке. Стрелки вместо перетаскивания сознательно:
   * на телефоне перетаскивание внутри прокручиваемой страницы почти не работает,
   * а «вверх/вниз» попадает пальцем с первого раза.
   */
  onMove?: (item: ItemDto, direction: -1 | 1) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}

/** Сколько держится подсветка узловой точки. */
const МИГ_СОБЫТИЯ_МС = 900;

/**
 * Признак, который поднимается на миг СМЕНЫ значения с ложного на истинное.
 *
 * Движение в приложении оправдано только в узловой точке: подарок забронирован,
 * складчина собрана. Анимация, привязанная к самому состоянию, сыграет и при
 * загрузке страницы — и человек, открывший список с десятью старыми бронями,
 * получит десять поздравлений разом с тем, что случилось неделю назад. Поэтому
 * здесь именно ПЕРЕХОД, а не факт.
 *
 * Первая отрисовка не считается сменой никогда: прежнего значения ещё нет, и
 * «сменилось» тут значило бы «страница открылась». Обратный ход (бронь сняли)
 * тоже не движение — праздновать нечего.
 */
function useПереход(значение: boolean, мс = МИГ_СОБЫТИЯ_МС): boolean {
  const прежнее = useRef<boolean | null>(null);
  const [горит, поджечь] = useState(false);

  useEffect(() => {
    const было = прежнее.current;
    прежнее.current = значение;

    // Первый ход: запоминаем и молчим.
    if (было === null) return;
    if (!(значение && !было)) return;

    поджечь(true);
    const таймер = setTimeout(() => поджечь(false), мс);
    return () => clearTimeout(таймер);
  }, [значение, мс]);

  return горит;
}

export function ItemCard({
  item,
  isOwner,
  canReserve,
  onEdit,
  onDelete,
  onReserve,
  onCancelReservation,
  busy,
  onMove,
  canMoveUp,
  canMoveDown,
}: ItemCardProps): JSX.Element {
  const [imageFailed, setImageFailed] = useState(false);

  const reservation = item.reservation;
  const mine = reservation?.mine ?? null;
  const fullyTaken = isFullyTaken(item);
  const host = hostOf(item.url);
  const favicon = faviconFor(item.url);
  const hasImage = Boolean(item.imageUrl) && !imageFailed;
  const вспышкаБрони = useПереход(Boolean(mine));

  const классыКарточки = [
    'item',
    fullyTaken && !mine ? 'item--taken' : '',
    вспышкаБрони ? 'item--zabronirovano' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={классыКарточки}>
      <div className={`item__media ${hasImage ? '' : 'item__media--empty'}`.trim()}>
        {hasImage ? (
          <img
            className="item__image"
            src={item.imageUrl ?? undefined}
            alt={item.title}
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          /*
           * Обложка вместо фотографии.
           *
           * Раньше здесь у всех желаний стоял один и тот же серый подарок, и
           * список из шести карточек читался как страница, которая не
           * догрузилась. Теперь у каждого желания свой оттенок и своя буква:
           * карточки различаются с одного взгляда, а пустота выглядит
           * задуманной. Буква — самое крупное, что есть у желания без снимка,
           * поэтому она и взята опорой; подарок остаётся тонкой линией сбоку,
           * чтобы карточка не читалась как аватар человека.
           */
          <div
            className="item__cover"
            style={{ ['--cover-hue' as string]: String(hueFromTitle(item.title)) }}
            aria-hidden="true"
          >
            <span className="item__cover-letter">{firstGrapheme(item.title)}</span>
            <span className="item__cover-mark">
              <IconGiftOutline size={22} />
            </span>
          </div>
        )}

        {item.quantity > 1 && (
          <div className="item__badges">
            <span />
            <span>
              <Badge tone="solid">× {item.quantity}</Badge>
            </span>
          </div>
        )}

        {/*
         * Правка и перестановка — на обложке, а не под текстом.
         *
         * Внизу карточки они стояли в один ряд с четырьмя одинаковыми серыми
         * значками и отбирали у карточки целую строку; при этом попасть в них
         * пальцем было трудно — 24 точки против рекомендованных 44. Здесь они
         * лежат поверх обложки, где места не жалко: на мыши всплывают при
         * наведении и при переходе клавишей (`:focus-within`), на касании
         * видны всегда — на телефоне наведения не существует.
         */}
        {isOwner && (
          <div className="item__tools">
            {onMove && (canMoveUp || canMoveDown) && (
              <>
                <button
                  type="button"
                  className="item__tool"
                  aria-label="Поднять выше"
                  disabled={!canMoveUp}
                  onClick={() => onMove(item, -1)}
                >
                  <IconUp size={18} />
                </button>
                <button
                  type="button"
                  className="item__tool"
                  aria-label="Опустить ниже"
                  disabled={!canMoveDown}
                  onClick={() => onMove(item, 1)}
                >
                  <IconDown size={18} />
                </button>
              </>
            )}
            <button type="button" className="item__tool" aria-label="Изменить" onClick={() => onEdit?.(item)}>
              <IconEdit size={18} />
            </button>
            <button
              type="button"
              className="item__tool item__tool--danger"
              aria-label="Удалить"
              onClick={() => onDelete?.(item)}
            >
              <IconTrash size={18} />
            </button>
          </div>
        )}
      </div>

      <div className="item__body">
        <h3 className="item__title" title={item.title}>
          {item.url ? (
            <a href={item.url} target="_blank" rel="noreferrer noopener" style={{ color: 'inherit' }}>
              {item.title}
            </a>
          ) : (
            item.title
          )}
        </h3>

        <PriorityScale priority={item.priority} />

        {item.priceMinor !== null && (
          <div className="item__price">{formatPrice(item.priceMinor, item.currency)}</div>
        )}

        {(item.sourceSite || host) && (
          <div className="item__source">
            {favicon && <Favicon src={favicon} />}
            <span className="truncate">{item.sourceSite ?? host}</span>
          </div>
        )}

        {item.description && (
          <p className="tiny muted" style={{ marginTop: 2 }}>
            {item.description.length > 90 ? `${item.description.slice(0, 90)}…` : item.description}
          </p>
        )}

      </div>

      {/*
       * Подвал остаётся только у дарителя: у владельца правка переехала на
       * обложку, и пустой подвал отнимал бы у карточки строку впустую.
       */}
      {!isOwner && (
        <div className="item__footer">
          <ReservationControls
            item={item}
            canReserve={canReserve}
            busy={busy}
            onReserve={onReserve}
            onCancelReservation={onCancelReservation}
          />
        </div>
      )}
    </article>
  );
}

/**
 * Степень хотения — пять точек, залитых по уровню.
 *
 * Раньше это был бейдж поверх фотографии, и то не для всех уровней:
 * «мечта» и «очень хочу» получали плашку в углу снимка, а «хочу» и
 * «было бы приятно» — ничего или мелкую серую подпись под текстом. Плашка
 * закрывала часть фотографии, а непоказанные уровни выглядели так, будто
 * их вообще нет. Теперь один и тот же индикатор для всех пяти уровней,
 * и стоит он в теле карточки — фотография остаётся чистой.
 */
function PriorityScale({ priority }: { priority: ItemDto['priority'] }): JSX.Element {
  const step = ITEM_PRIORITY_STEP[priority];
  return (
    <div className="item__priority" title={ITEM_PRIORITY_LABEL[priority]}>
      <span className="item__priority-dots" aria-hidden="true">
        {ITEM_PRIORITIES.map((value) => (
          <span
            key={value}
            className={`item__priority-dot${ITEM_PRIORITY_STEP[value] <= step ? ' item__priority-dot--filled' : ''}`}
          />
        ))}
      </span>
      <span className="tiny muted">{ITEM_PRIORITY_LABEL[priority]}</span>
    </div>
  );
}

/**
 * Значок сайта.
 *
 * Иконку отдаёт сторонний сервис, и он не обязан знать каждый домен: на редких
 * магазинах он отвечает ошибкой. Показывать вместо значка «сломанную картинку»
 * браузера — хуже, чем не показывать ничего, поэтому при неудаче просто убираем
 * его: подпись с названием сайта рядом остаётся и несёт весь смысл.
 */
function Favicon({ src }: { src: string }): JSX.Element | null {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return <img className="item__favicon" src={src} alt="" loading="lazy" onError={() => setFailed(true)} />;
}

function ReservationControls({
  item,
  canReserve,
  busy,
  onReserve,
  onCancelReservation,
}: Pick<ItemCardProps, 'item' | 'canReserve' | 'busy' | 'onReserve' | 'onCancelReservation'>): JSX.Element | null {
  const reservation = item.reservation;

  /*
   * Доля сбора считается ЗДЕСЬ, до всех досрочных возвратов, и по одной
   * формуле на все ветки: крючок ниже нельзя звать после `return`, а две
   * копии формулы разъехались бы при первой же правке.
   */
  const цель = item.priceMinor ?? 0;
  const percent =
    item.groupGift && reservation && цель > 0
      ? Math.min(100, Math.round((reservation.collectedMinor / цель) * 100))
      : 0;
  const вспышкаСбора = useПереход(percent >= 100);

  // Гость без аккаунта: броней не видит и бронировать не может.
  if (!reservation) {
    return canReserve ? null : <span className="tiny faint">Войдите, чтобы забронировать</span>;
  }

  const { mine, others, anonymousCount, remaining, reservedCount } = reservation;

  // ─── Складчина: показываем общий сбор, а не «занято / свободно» ───
  if (item.groupGift) {
    const { collectedMinor, contributorCount } = reservation;
    const goal = цель;

    return (
      <div className="stack stack--sm">
        <Badge tone="accent">🤝 Скидываемся вместе</Badge>

        {goal > 0 && (
          <>
            <div
              className="progress"
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Собрано"
            >
              <div
                className={[
                  'progress__fill',
                  percent >= 100 ? 'progress__fill--done' : '',
                  вспышкаСбора ? 'progress__fill--sobrano' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ width: `${percent}%` }}
              />
            </div>
            <span className="tiny muted">
              Собрано {formatPrice(collectedMinor, item.currency)} из {formatPrice(goal, item.currency)}
              {вспышкаСбора && <strong className="sobrali"> Собрали!</strong>}
            </span>
          </>
        )}

        {contributorCount > 0 && (
          <span className="tiny faint">
            {pluralize(contributorCount, 'участник', 'участника', 'участников')}
          </span>
        )}
        <GiftGivers others={others} anonymousCount={anonymousCount} />

        {mine ? (
          <>
            <Badge tone="success">
              Ваш вклад{mine.contributionMinor ? `: ${formatPrice(mine.contributionMinor, item.currency)}` : ''}
            </Badge>
            <Button size="sm" variant="ghost" onClick={() => onCancelReservation?.(item)} loading={busy} block>
              Выйти из складчины
            </Button>
          </>
        ) : (
          <Button size="sm" variant="primary" onClick={() => onReserve?.(item)} loading={busy} block>
            Скинуться
          </Button>
        )}
      </div>
    );
  }

  if (mine) {
    return (
      <div className="stack stack--sm">
        <Badge tone="success">✓ Дарите вы{mine.quantity > 1 ? ` (${mine.quantity} шт.)` : ''}</Badge>
        <Button size="sm" variant="ghost" onClick={() => onCancelReservation?.(item)} loading={busy} block>
          Отменить бронь
        </Button>
      </div>
    );
  }

  if (remaining === 0) {
    return (
      <div className="stack stack--sm">
        <Badge tone="warning">Уже разобрали</Badge>
        <GiftGivers others={others} anonymousCount={anonymousCount} />
      </div>
    );
  }

  return (
    <div className="stack stack--sm">
      {reservedCount > 0 && (
        <>
          <span className="tiny muted">
            Взяли {reservedCount} из {item.quantity} — осталось {remaining}
          </span>
          <GiftGivers others={others} anonymousCount={anonymousCount} />
        </>
      )}
      <Button size="sm" variant="primary" onClick={() => onReserve?.(item)} loading={busy} block>
        Дарю это
      </Button>
    </div>
  );
}

function GiftGivers({
  others,
  anonymousCount,
}: {
  others: NonNullable<ItemDto['reservation']>['others'];
  anonymousCount: number;
}): JSX.Element | null {
  if (others.length === 0 && anonymousCount === 0) return null;

  return (
    <div className="row" style={{ gap: 'var(--space-2)' }}>
      {others.length > 0 && (
        <div className="avatar-stack">
          {others.slice(0, 3).map((entry) => (
            <Avatar key={entry.user.id} name={entry.user.displayName} src={entry.user.avatarUrl} size="sm" />
          ))}
        </div>
      )}
      <span className="tiny faint truncate">
        {others.length > 0 && others.map((entry) => entry.user.displayName).join(', ')}
        {others.length > 0 && anonymousCount > 0 && ' и '}
        {anonymousCount > 0 && `${pluralize(anonymousCount, 'человек', 'человека', 'человек')} анонимно`}
      </span>
    </div>
  );
}
